import React, { useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Dimensions,
  Alert,
  Modal,
  TouchableOpacity,
  FlatList
} from 'react-native';
import {
  Text,
  Card,
  Title,
  Paragraph,
  TextInput,
  Button,
  Surface,
  Chip,
  Divider
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import {
  colors, 
  spacing, 
  borderRadius, 
  shadows, 
  typography,
  commonStyles,
  getCardMaxWidth,
  getPadding 
} from './sharedStyles';

export default function PersonalMonthComponent() {
  const [birthDate, setBirthDate] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [monthModalVisible, setMonthModalVisible] = useState(false);
  const [result, setResult] = useState(null);

  const { width } = Dimensions.get('window');
  const isDesktop = width >= 1024;
  const isMobile = width < 768;

  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const formatBirthDate = (text) => {
    const numbers = text.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 4)}-${numbers.slice(4, 8)}`;
    }
  };

  const calculatePersonalYear = (birthDateStr, targetYear) => {
    if (!birthDateStr || !targetYear) return null;
    
    const yearNum = parseInt(targetYear);
    if (isNaN(yearNum)) return null;

    let birthMonth, birthDay;
    if (birthDateStr.includes('-')) {
      const parts = birthDateStr.split('-');
      if (parts.length !== 3) return null;
      birthMonth = parseInt(parts[0]);
      birthDay = parseInt(parts[1]);
    } else if (birthDateStr.includes('/')) {
      const parts = birthDateStr.split('/');
      if (parts.length !== 3) return null;
      birthMonth = parseInt(parts[0]);
      birthDay = parseInt(parts[1]);
    } else {
      return null;
    }

    if (isNaN(birthMonth) || isNaN(birthDay)) return null;

    let sum = birthMonth + birthDay + yearNum;
    
    while (sum > 9 && sum !== 11 && sum !== 22) {
      sum = sum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    }
    
    return sum;
  };

  const calculatePersonalMonth = (personalYear, currentMonth) => {
    if (!personalYear || !currentMonth) return null;
    
    const monthNum = parseInt(currentMonth);
    if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return null;
    
    let sum = personalYear + monthNum;
    const originalSum = sum;
    
    while (sum > 9 && sum !== 11 && sum !== 22) {
      sum = sum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    }
    
    return {
      personalYear,
      month: monthNum,
      personalMonth: sum,
      calculation: `Personal Year ${personalYear} + Month ${monthNum} = ${originalSum}` + 
                   (sum !== originalSum ? ` → ${sum}` : '')
    };
  };

  const getPersonalMonthMeaning = (number) => {
    const meanings = {
      1: {
        title: "Month of New Initiatives",
        description: "Time to start new projects, take leadership roles, and make fresh beginnings. Your energy is high and focused on independence and innovation.",
        themes: ["New starts", "Leadership", "Independence", "Innovation"],
        advice: "Start that project you've been thinking about. Take initiative and lead by example."
      },
      2: {
        title: "Month of Cooperation",
        description: "Focus on partnerships, patience, and working with others. This month rewards collaboration and diplomatic approaches to challenges.",
        themes: ["Partnerships", "Patience", "Cooperation", "Diplomacy"],
        advice: "Build bridges, be patient, and work harmoniously with others. Perfect time for team projects."
      },
      3: {
        title: "Month of Creative Expression",
        description: "Express yourself through creativity, communication, and social activities. This is an excellent month for artistic pursuits and expanding your network.",
        themes: ["Creativity", "Communication", "Social life", "Self-expression"],
        advice: "Be creative, communicate your ideas, and enjoy social gatherings. Express your authentic self."
      },
      4: {
        title: "Month of Hard Work",
        description: "Focus on practical matters, organization, and building solid foundations. This month rewards discipline, attention to detail, and systematic approaches.",
        themes: ["Hard work", "Organization", "Discipline", "Foundations"],
        advice: "Get organized, work steadily towards your goals, and pay attention to details. Build for the future."
      },
      5: {
        title: "Month of Change and Freedom",
        description: "Embrace change, seek new experiences, and break free from routine. This month brings opportunities for adventure, travel, and personal expansion.",
        themes: ["Change", "Freedom", "Adventure", "Variety"],
        advice: "Welcome change, take calculated risks, and explore new possibilities. Avoid rigid schedules."
      },
      6: {
        title: "Month of Responsibility",
        description: "Focus on family, home, and community responsibilities. This month emphasizes nurturing relationships and caring for those around you.",
        themes: ["Family", "Home", "Responsibility", "Nurturing"],
        advice: "Take care of family matters, focus on your home environment, and be there for loved ones."
      },
      7: {
        title: "Month of Reflection",
        description: "Turn inward for study, meditation, and spiritual growth. This month favors introspection, research, and developing your inner wisdom.",
        themes: ["Reflection", "Study", "Spirituality", "Inner work"],
        advice: "Meditate, study something meaningful, and trust your intuition. Take time for inner development."
      },
      8: {
        title: "Month of Material Success",
        description: "Focus on career, business, and material achievements. This month rewards ambition, organization, and practical business decisions.",
        themes: ["Success", "Business", "Achievement", "Power"],
        advice: "Focus on career advancement, make important business moves, and organize your finances."
      },
      9: {
        title: "Month of Completion",
        description: "Time to complete projects, let go of what's not working, and prepare for new cycles. This month emphasizes service to others and humanitarian efforts.",
        themes: ["Completion", "Release", "Service", "Wisdom"],
        advice: "Complete unfinished business, help others, and prepare for new beginnings next month."
      },
      11: {
        title: "Month of Spiritual Insight",
        description: "A master number month bringing spiritual illumination, intuition, and inspiration. Focus on your higher purpose and spiritual development.",
        themes: ["Illumination", "Intuition", "Inspiration", "Spiritual growth"],
        advice: "Trust your intuition completely, share your spiritual insights, and inspire others."
      },
      22: {
        title: "Month of Master Manifestation",
        description: "A master number month for achieving something significant. Focus on large-scale projects and turning big visions into reality.",
        themes: ["Master building", "Big visions", "Achievement", "Manifestation"],
        advice: "Think big, work on significant projects, and manifest your grandest visions into reality."
      }
    };
    
    return meanings[number] || { title: "Unknown", description: "", themes: [], advice: "" };
  };

  const handleCalculate = () => {
    if (!birthDate.trim()) {
      Alert.alert('Error', 'Please enter your birth date');
      return;
    }

    if (!year.trim()) {
      Alert.alert('Error', 'Please enter a year');
      return;
    }

    if (!month) {
      Alert.alert('Error', 'Please select a month');
      return;
    }

    const personalYear = calculatePersonalYear(birthDate, year);
    if (!personalYear) {
      Alert.alert('Error', 'Please enter a valid birth date (MM-DD-YYYY format)');
      return;
    }

    const calculation = calculatePersonalMonth(personalYear, month);
    if (!calculation) {
      Alert.alert('Error', 'Unable to calculate personal month');
      return;
    }

    setResult({
      ...calculation,
      monthName: months.find(m => m.value === month)?.label || 'Unknown',
      year: parseInt(year)
    });
  };

  const resetCalculation = () => {
    setBirthDate('');
    setYear(new Date().getFullYear().toString());
    setMonth((new Date().getMonth() + 1).toString());
    setResult(null);
  };

  const renderMonth = ({ item }) => (
    <TouchableOpacity
      style={styles.monthItem}
      onPress={() => {
        setMonth(item.value);
        setMonthModalVisible(false);
      }}
    >
      <Text style={[
        styles.monthItemText,
        month === item.value && styles.monthItemTextSelected
      ]}>
        {item.label}
      </Text>
      {month === item.value && (
        <Ionicons name="checkmark" size={24} color="#4a3975" />
      )}
    </TouchableOpacity>
  );

  return (
    <View style={commonStyles.container}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.mainContent}>
          {/* Input Section */}
          <Surface style={[commonStyles.solidCard, styles.inputCard]}>
            {/* Header Section */}
            <View style={styles.headerContainer}>
              <LinearGradient
                colors={['rgba(74, 57, 117, 0.1)', 'rgba(102, 126, 234, 0.1)']}
                style={styles.headerIconContainer}
              >
                <Ionicons name="calendar-outline" size={32} color={colors.primary} />
              </LinearGradient>
              <Title style={styles.headerTitle}>Personal Month Calculator</Title>
              <Text style={styles.headerSubtitle}>
                Discover your personal numerological cycle for specific months
              </Text>
            </View>

            <View style={styles.inputFieldContainer}>
              <View style={styles.fieldWrapper}>
                <Text style={styles.fieldLabel}>Birth Date</Text>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={birthDate}
                      onChangeText={(text) => setBirthDate(formatBirthDate(text))}
                      selectTextOnFocus={true}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="MM-DD-YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={10}
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>

              <View style={styles.fieldWrapper}>
                <Text style={styles.fieldLabel}>Year</Text>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar-outline" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={year}
                      onChangeText={setYear}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={4}
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>

              <View style={styles.fieldWrapper}>
                <Text style={styles.fieldLabel}>Month</Text>
                <TouchableOpacity
                  onPress={() => setMonthModalVisible(true)}
                  style={styles.monthSelector}
                >
                  <Surface style={styles.monthSelectorSurface}>
                    <View style={styles.monthSelectorContent}>
                      <Ionicons 
                        name="calendar-clear" 
                        size={20} 
                        color={colors.primary} 
                        style={styles.inputIcon} 
                      />
                      <Text style={styles.monthSelectorText}>
                        {months.find(m => m.value === month)?.label || 'Select Month'}
                      </Text>
                      <Ionicons name="chevron-down" size={20} color={colors.primary} />
                    </View>
                  </Surface>
                </TouchableOpacity>
              </View>

              <View style={styles.buttonContainer}>
                <LinearGradient
                  colors={[colors.primary, colors.primaryLight]}
                  style={styles.gradientButton}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                >
                  <Button
                    mode="contained"
                    onPress={handleCalculate}
                    style={styles.calculateButton}
                    labelStyle={styles.calculateButtonText}
                    contentStyle={styles.calculateButtonContent}
                    buttonColor="transparent"
                  >
                    <Ionicons name="analytics" size={18} color={colors.white} style={styles.buttonIcon} />
                    Calculate Personal Month
                  </Button>
                </LinearGradient>
                
                {result && (
                  <Button
                    mode="outlined"
                    onPress={resetCalculation}
                    style={styles.resetButton}
                    labelStyle={styles.resetButtonText}
                  >
                    <Ionicons name="refresh" size={16} color={colors.primary} />
                    Reset
                  </Button>
                )}
              </View>
            </View>
          </Surface>

          {result && (
            <View style={styles.resultsContainer}>
              <Surface style={[styles.resultCard, isDesktop && styles.desktopResultCard]}>
                <View style={styles.resultHeader}>
                  <Title style={styles.monthYearTitle}>{result.monthName} {result.year}</Title>
                  <LinearGradient
                    colors={[colors.primary, colors.primaryLight]}
                    style={styles.personalMonthBadge}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <Text style={styles.personalMonthNumber}>{result.personalMonth}</Text>
                    <Text style={styles.personalMonthLabel}>Personal Month</Text>
                  </LinearGradient>
                </View>
                
                <Card style={styles.calculationCard}>
                  <Card.Content>
                    <Text style={styles.calculationLabel}>Calculation:</Text>
                    <Text style={styles.calculationText}>{result.calculation}</Text>
                  </Card.Content>
                </Card>
              </Surface>

              <Card style={[styles.meaningCard, isDesktop && styles.desktopMeaningCard]}>
                <Card.Content>
                  <Title style={styles.meaningTitle}>
                    {getPersonalMonthMeaning(result.personalMonth).title}
                  </Title>
                  <Paragraph style={styles.meaningDescription}>
                    {getPersonalMonthMeaning(result.personalMonth).description}
                  </Paragraph>
                  
                  <View style={styles.themesContainer}>
                    <Text style={styles.themesTitle}>Key Themes:</Text>
                    <View style={styles.chipContainer}>
                      {getPersonalMonthMeaning(result.personalMonth).themes.map((theme, index) => (
                        <Chip 
                          key={index} 
                          style={styles.themeChip}
                          textStyle={styles.themeChipText}
                        >
                          {theme}
                        </Chip>
                      ))}
                    </View>
                  </View>

                  <Surface style={styles.adviceCard}>
                    <View style={styles.adviceHeader}>
                      <Ionicons name="bulb-outline" size={24} color="#4a3975" />
                      <Text style={styles.adviceTitle}>Your Monthly Focus:</Text>
                    </View>
                    <Paragraph style={styles.adviceText}>
                      {getPersonalMonthMeaning(result.personalMonth).advice}
                    </Paragraph>
                  </Surface>
                </Card.Content>
              </Card>
            </View>
          )}
        </View>
      </ScrollView>

      <Modal
        animationType="slide"
        transparent={true}
        visible={monthModalVisible}
        onRequestClose={() => setMonthModalVisible(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setMonthModalVisible(false)}
        >
          <View style={[styles.modalContent, isDesktop && styles.desktopModalContent]}>
            <Title style={styles.modalTitle}>Select Month</Title>
            <Divider style={styles.modalDivider} />
            <FlatList
              data={months}
              renderItem={renderMonth}
              keyExtractor={(item) => item.value}
              style={styles.monthList}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    padding: spacing.lg,
  },
  mainContent: {
    alignItems: 'center',
    paddingBottom: spacing.xxl,
  },
  
  // Input Section
  inputCard: {
    width: '100%',
    maxWidth: getCardMaxWidth(600),
    alignSelf: 'center',
    marginBottom: spacing.xxl,
    padding: spacing.xxl,
  },
  
  // Header Section
  headerContainer: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: {
    ...typography.heading1,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  headerSubtitle: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 22,
  },
  
  // Input Section
  inputFieldContainer: {
    gap: spacing.xl,
  },
  fieldWrapper: {
    gap: spacing.sm,
  },
  fieldLabel: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
    marginLeft: spacing.sm,
  },
  inputWrapper: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    ...shadows.input,
    overflow: 'hidden',
  },
  inputWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    paddingVertical: spacing.xs,
  },
  inputIcon: {
    position: 'absolute',
    left: spacing.lg,
    zIndex: 1,
  },
  modernInput: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingLeft: spacing.xxxl + spacing.sm,
    elevation: 0,
    shadowOpacity: 0,
  },
  
  // Month Selector
  monthSelector: {
    // No additional styles needed - TouchableOpacity wrapper
  },
  monthSelectorSurface: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    ...shadows.input,
    overflow: 'hidden',
  },
  monthSelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  monthSelectorText: {
    flex: 1,
    ...typography.body,
    color: colors.textPrimary,
    marginLeft: spacing.lg,
  },
  
  // Button Section
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
    marginTop: spacing.xl,
  },
  gradientButton: {
    borderRadius: borderRadius.xl,
    ...shadows.button,
    flex: 1,
    maxWidth: 300,
  },
  calculateButton: {
    backgroundColor: 'transparent',
    elevation: 0,
    shadowOpacity: 0,
  },
  calculateButtonContent: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  calculateButtonText: {
    ...typography.button,
    color: colors.white,
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
  resetButton: {
    borderColor: colors.primary,
    borderRadius: borderRadius.xl,
    backgroundColor: 'transparent',
  },
  resetButtonText: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
  },
  
  // Results Section
  resultsContainer: {
    width: '100%',
    maxWidth: getCardMaxWidth(700),
    alignSelf: 'center',
    gap: spacing.xxl,
  },
  resultCard: {
    ...commonStyles.resultCard,
    alignItems: 'center',
  },
  resultHeader: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  monthYearTitle: {
    ...typography.heading1,
    fontSize: 38,
    color: colors.primary,
    marginBottom: spacing.lg,
  },
  personalMonthBadge: {
    borderRadius: 35,
    paddingHorizontal: spacing.xxxl,
    paddingVertical: spacing.xl,
    alignItems: 'center',
    marginBottom: spacing.lg,
    ...shadows.result,
    overflow: 'hidden',
  },
  personalMonthNumber: {
    ...typography.heading1,
    fontSize: 52,
    color: colors.white,
    fontWeight: '800',
  },
  personalMonthLabel: {
    ...typography.caption,
    color: colors.white,
    fontWeight: '600',
    marginTop: spacing.xs,
  },
  calculationCard: {
    backgroundColor: colors.primary + '10',
    borderRadius: borderRadius.lg,
    ...shadows.input,
  },
  calculationLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  calculationText: {
    ...typography.heading3,
    color: colors.primary,
  },
  meaningCard: {
    ...commonStyles.resultCard,
  },
  meaningTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  meaningDescription: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xxl,
  },
  themesContainer: {
    marginBottom: spacing.xxl,
  },
  themesTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginBottom: spacing.lg,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  themeChip: {
    backgroundColor: colors.primary + '15',
    borderRadius: borderRadius.pill,
    ...shadows.input,
  },
  themeChipText: {
    color: colors.primary,
    fontWeight: '600',
  },
  adviceCard: {
    backgroundColor: colors.primary + '05',
    borderRadius: borderRadius.lg,
    padding: spacing.xl,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  adviceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  adviceTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginLeft: spacing.md,
  },
  adviceText: {
    ...typography.body,
    color: colors.textSecondary,
    lineHeight: 24,
  },
  
  // Modal Styles
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    padding: spacing.xxl,
    width: '90%',
    maxHeight: '70%',
    ...shadows.result,
  },
  desktopModalContent: {
    maxWidth: 400,
  },
  modalTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  modalDivider: {
    marginBottom: spacing.lg,
  },
  monthList: {
    maxHeight: 400,
  },
  monthItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.xs,
  },
  monthItemText: {
    ...typography.body,
    color: colors.textPrimary,
  },
  monthItemTextSelected: {
    fontWeight: '700',
    color: colors.primary,
  },
});